import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PartnerLinks } from '../footer/PartnerLinks';
import { BrandBadges } from '../footer/BrandBadges';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // 监听滚动事件，添加阴影效果
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toolLinks = [
    { to: '/chinese-name-generator', label: 'Chinese Name Generator' },
    { to: '/name-meanings', label: 'Chinese Name Meanings' },
    { to: '/pronunciation', label: 'Chinese Name Pronunciation' },
  ];

  const categoryLinks = [
    { to: '/chinese-boy-names', label: 'Chinese Boy Names' },
    { to: '/chinese-girl-names', label: 'Chinese Girl Names' },
    { to: '/chinese-last-names', label: 'Chinese Last Names' },
  ];

  const topicLinks = [
    { to: '/cute-chinese-girl-names', label: 'Cute Chinese Girl Names' },
    { to: '/funny-chinese-names', label: 'Funny Chinese Names' },
    { to: '/chinese-pet-names', label: 'Chinese Pet Names' },
    { to: '/chinese-dragon-names', label: 'Chinese Dragon Names' },
  ];

  const learnLinks = [
    { to: '/blog', label: 'Blog & Guides' },
    { to: '/cultural-insights', label: 'Chinese Naming Culture' },
    { to: '/blog/how-to-choose-chinese-name', label: 'How to Choose a Chinese Name' },
    { to: '/faq', label: 'FAQ' },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-200 ${
        isScrolled ? 'shadow-md' : 'shadow-sm'
      }`}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img 
                src="/favicon.png" 
                alt="Chinese Name Generator Logo" 
                className="h-10 w-10 object-contain"
              />
              <span className="ml-2 text-xl font-bold text-gray-900">Chinese Name Generator</span>
            </Link>
          </div>
          <div className="hidden md:flex items-center space-x-1">
            <Link to="/" className="px-3 py-2 text-gray-600 hover:text-indigo-600 transition-colors font-medium">Home</Link>
            <Link to="/chinese-name-generator" className="px-3 py-2 text-gray-600 hover:text-indigo-600 transition-colors font-medium">Generator</Link>
            
            <div className="relative group">
              <span className="px-3 py-2 text-gray-600 font-medium cursor-default">Categories</span>
              <div className="absolute left-0 mt-2 w-64 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-1">
                  {categoryLinks.map((link) => (
                    <Link key={link.to} to={link.to} className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600">
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative group">
              <span className="px-3 py-2 text-gray-600 font-medium cursor-default">Topics</span>
              <div className="absolute left-0 mt-2 w-72 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-1">
                  {topicLinks.map((link) => (
                    <Link key={link.to} to={link.to} className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600">
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative group">
              <span className="px-3 py-2 text-gray-600 font-medium cursor-default">Learn</span>
              <div className="absolute left-0 mt-2 w-72 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-1">
                  {learnLinks.map((link) => (
                    <Link key={link.to} to={link.to} className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600">
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
            >
              <span className="sr-only">Open menu</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        <div className={`md:hidden transition-all duration-200 ease-in-out ${isMobileMenuOpen ? 'max-h-[42rem]' : 'max-h-0 overflow-hidden'}`}>
          <div className="px-2 pt-2 pb-3 space-y-1 bg-white shadow-lg">
            <Link to="/" className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
              Home
            </Link>
            <Link to="/chinese-name-generator" className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
              Chinese Name Generator
            </Link>
            <div className="px-3 py-2">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Tools</div>
              {toolLinks.map((link) => (
                <Link key={link.to} to={link.to} className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="px-3 py-2">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Categories</div>
              {categoryLinks.map((link) => (
                <Link key={link.to} to={link.to} className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="px-3 py-2">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Topics</div>
              {topicLinks.map((link) => (
                <Link key={link.to} to={link.to} className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="px-3 py-2">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Learn</div>
              {learnLinks.map((link) => (
                <Link key={link.to} to={link.to} className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Add padding to main content to prevent it from being hidden under the fixed header */}
      <main className="flex-grow pt-16">
        {children}
      </main>


      <footer className="bg-gray-50">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Core Tools</h3>
              <div className="mt-4 space-y-4">
                <Link to="/" className="text-base text-gray-500 hover:text-gray-900 block">Home</Link>
                <Link to="/chinese-name-generator" className="text-base text-gray-500 hover:text-gray-900 block">Chinese Name Generator</Link>
                <Link to="/name-meanings" className="text-base text-gray-500 hover:text-gray-900 block">Chinese Name Meanings</Link>
                <Link to="/pronunciation" className="text-base text-gray-500 hover:text-gray-900 block">Chinese Name Pronunciation</Link>
                <Link to="/faq" className="text-base text-gray-500 hover:text-gray-900 block">FAQ</Link>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Name Categories</h3>
              <div className="mt-4 space-y-4">
                <Link to="/chinese-boy-names" className="text-base text-gray-500 hover:text-gray-900 block">Chinese Boy Names</Link>
                <Link to="/chinese-girl-names" className="text-base text-gray-500 hover:text-gray-900 block">Chinese Girl Names</Link>
                <Link to="/chinese-last-names" className="text-base text-gray-500 hover:text-gray-900 block">Chinese Last Names</Link>
                <Link to="/cultural-insights" className="text-base text-gray-500 hover:text-gray-900 block">Chinese Naming Culture</Link>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Special Topics</h3>
              <div className="mt-4 space-y-4">
                <Link to="/cute-chinese-girl-names" className="text-base text-gray-500 hover:text-gray-900 block">Cute Chinese Girl Names</Link>
                <Link to="/funny-chinese-names" className="text-base text-gray-500 hover:text-gray-900 block">Funny Chinese Names</Link>
                <Link to="/chinese-pet-names" className="text-base text-gray-500 hover:text-gray-900 block">Chinese Pet Names</Link>
                <Link to="/chinese-dragon-names" className="text-base text-gray-500 hover:text-gray-900 block">Chinese Dragon Names</Link>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Guides & Blog</h3>
              <div className="mt-4 space-y-4">
                <Link to="/blog" className="text-base text-gray-500 hover:text-gray-900 block">Chinese Names Blog</Link>
                <Link to="/blog/how-to-choose-chinese-name" className="text-base text-gray-500 hover:text-gray-900 block">How to Choose a Chinese Name</Link>
                <Link to="/blog/chinese-names-meanings" className="text-base text-gray-500 hover:text-gray-900 block">Chinese Names and Meanings</Link>
                <Link to="/blog/chinese-names-for-foreigners" className="text-base text-gray-500 hover:text-gray-900 block">Chinese Names for Foreigners</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Brand Badges */}
      <BrandBadges />

      {/* Partner Links */}
      <PartnerLinks />
    </div>
  );
}
